import { BaseListStateService, IFilterControls } from 'src/app/shared/models/base.state.service';
import { PtsPaymentTerminal, PtsPaymentTerminalSearchEvent, TerminalConnectionStatus, TerminalStatus } from '../pts-payment-terminal-model';
import { FormControl, FormGroup } from '@angular/forms';
import { PtsPaymentTerminalService } from 'src/app/shared/services/payment-terminal.service';
import { inject, signal } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { SnackbarService } from 'ok-angular-shared/components';
import { PagedResult } from 'src/app/shared/models/paging';

interface IPtsTerminalsFilterValues {
  query: string | null;
  connectionStatus: TerminalConnectionStatus | null;
  statuses: TerminalStatus[] | null;
  chargingStationLocationIdentifier: string | null;
  payterTerminalLocation: string | null;
  serialNumber: string | null;
}

export enum PtsTerminalsQueryParams {
  query = 'query',
  connectionStatus = 'connectionStatus',
  statuses = 'statuses',
  chargingStationLocationIdentifier = 'chargingStationLocationIdentifier',
  payterTerminalLocation = 'payterTerminalLocation',
  serialNumber = 'serialNumber',
}

export class PtsTerminalsListStateService extends BaseListStateService<
  PtsPaymentTerminal,
  PtsTerminalsQueryParams,
  IPtsTerminalsFilterValues
> {
  service = inject(PtsPaymentTerminalService);
  focusedTerminal = signal<PtsPaymentTerminal | null>(null);

  private snackbarService = inject(SnackbarService);

  private queryParamValue(key: PtsTerminalsQueryParams): string | null {
    return this.queryParamMap$()?.get(key) ?? null;
  }

  protected createQueryParams(filters: Partial<IPtsTerminalsFilterValues>): Record<PtsTerminalsQueryParams, string | null> {
    const queryParams: Record<PtsTerminalsQueryParams, string | null> = {
      [PtsTerminalsQueryParams.chargingStationLocationIdentifier]: filters.chargingStationLocationIdentifier
        ? filters.chargingStationLocationIdentifier.toString()
        : null,
      [PtsTerminalsQueryParams.payterTerminalLocation]: filters.payterTerminalLocation ? filters.payterTerminalLocation.toString() : null,
      [PtsTerminalsQueryParams.statuses]:
        filters.statuses && filters.statuses.length > 0 ? filters.statuses.map(encodeURIComponent).join(',') : null,
      [PtsTerminalsQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
      [PtsTerminalsQueryParams.connectionStatus]: filters.connectionStatus ? encodeURIComponent(filters.connectionStatus) : null,
      [PtsTerminalsQueryParams.serialNumber]: filters.serialNumber ? encodeURIComponent(filters.serialNumber) : null,
    };
    return queryParams;
  }

  public resetTerminalsTrigger = signal<{ reset: boolean }>({ reset: false });

  form: FormGroup<IFilterControls<IPtsTerminalsFilterValues>> = new FormGroup<IFilterControls<IPtsTerminalsFilterValues>>({
    query: new FormControl<string | null>(
      this.queryParamValue(PtsTerminalsQueryParams.query)
        ? decodeURIComponent(this.queryParamValue(PtsTerminalsQueryParams.query) as string)
        : null
    ),
    serialNumber: new FormControl<string | null>(
      this.queryParamValue(PtsTerminalsQueryParams.serialNumber)
        ? decodeURIComponent(this.queryParamValue(PtsTerminalsQueryParams.serialNumber) as string)
        : null
    ),
    connectionStatus: new FormControl<TerminalConnectionStatus | null>(
      this.queryParamValue(PtsTerminalsQueryParams.connectionStatus)
        ? this.filteringService.safeCastEnum(
            TerminalConnectionStatus,
            this.queryParamValue(PtsTerminalsQueryParams.connectionStatus) as string
          )
        : null
    ),
    statuses: new FormControl<TerminalStatus[] | null>(
      this.queryParamValue(PtsTerminalsQueryParams.statuses)
        ? this.filteringService.safeCastEnumArray(TerminalStatus, this.queryParamValue(PtsTerminalsQueryParams.statuses) as string)
        : null
    ),
    chargingStationLocationIdentifier: new FormControl<string | null>(
      this.queryParamValue(PtsTerminalsQueryParams.chargingStationLocationIdentifier)
        ? decodeURIComponent(this.queryParamValue(PtsTerminalsQueryParams.chargingStationLocationIdentifier) as string)
        : null
    ),
    payterTerminalLocation: new FormControl<string | null>(
      this.queryParamValue(PtsTerminalsQueryParams.payterTerminalLocation)
        ? decodeURIComponent(this.queryParamValue(PtsTerminalsQueryParams.payterTerminalLocation) as string)
        : null
    ),
  });

  public resetFilter() {
    this.form.patchValue({
      query: null,
      chargingStationLocationIdentifier: null,
      connectionStatus: null,
      payterTerminalLocation: null,
      statuses: [],
      serialNumber: null,
    });
    this.viewState.update(current => ({
      ...current,
      sort: this.defaultSort,
      page: { length: 0, pageIndex: 0, pageSize: this.defaultPageSize },
    }));
    this.focusedTerminal.set(null);
    this.resetTerminalsTrigger.set({ reset: true });
  }

  terminals = signal<PagedResult<PtsPaymentTerminal>>({
    items: [],
    totalCount: 0,
    pageIndex: 0,
    pageSize: this.defaultPageSize,
  });

  private refreshTerminalSubject = new Subject<PtsPaymentTerminal>();

  constructor() {
    super();
    this.resetPageOnFilterChange();

    this.refreshTerminalSubject
      .pipe(
        switchMap(terminal =>
          combineLatest([
            this.service.getPaymentTerminals(<PtsPaymentTerminalSearchEvent>{
              query: terminal.serialNumber,
            }),
            this.service.getConnectorsByTerminalSerialNumbers([terminal.serialNumber]),
            this.service.getChargingStationInfoForTerminals([terminal.serialNumber]),
          ]).pipe(
            tap(([payterTerminal, connectors, chargingStationInfo]) => {
              if (!payterTerminal) return;

              const itemIndex = this.terminals().items.findIndex(s => s.identifier === terminal.identifier);
              if (itemIndex === -1) return;

              const highlight = new BehaviorSubject<boolean>(true);

              this.terminals.update(current => {
                const newItems = [...current.items];
                newItems.splice(itemIndex, 1, {
                  ...newItems[itemIndex],
                  name: payterTerminal.items[0].name,
                  online: payterTerminal.items[0].online,
                  statuses: payterTerminal.items[0].statuses,
                  connectorCount: connectors?.length ?? 0,
                  chargingStationLocationInfo: chargingStationInfo.find(c => c.serialNumber === payterTerminal.items[0].serialNumber),
                  highlight: highlight,
                });

                return {
                  ...current,
                  items: newItems,
                };
              });

              setTimeout(() => {
                highlight.next(false);

                // Writing to the signal (not just the BehaviorSubject) is required to trigger a
                // re-render in this zoneless app, so classFn re-evaluates and drops the highlight class.
                this.terminals.update(current => {
                  const latestIndex = current.items.findIndex(s => s.identifier === terminal.identifier);
                  if (latestIndex === -1) return current;

                  const items = [...current.items];
                  items.splice(latestIndex, 1, { ...items[latestIndex], highlight: new BehaviorSubject<boolean>(false) });

                  return { ...current, items };
                });
              }, 3000);
            }),
            catchError(error => {
              this.snackbarService.error('Fejl ved opdatering af betalingsterminal');
              return of(null);
            })
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe();

    combineLatest([
      this.form.valueChanges.pipe(startWith(this.form.value)),
      toObservable(this.viewState),
      toObservable(this.refreshTrigger),
    ])
      .pipe(
        tap(() => this.loading.set(true)),
        debounceTime(300),
        distinctUntilChanged(),
        tap(([form]) => {
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: this.createQueryParams(form),
            queryParamsHandling: 'merge',
          });
        }),
        switchMap(([filters, viewState]) => {
          const searchEvent = {
            query: filters.query,
            paging: viewState.page,
            sorting: viewState.sort,
            payterTerminalLocation: filters.payterTerminalLocation,
            terminalConnectionStatus: filters.connectionStatus,
            terminalStatuses: filters.statuses,
            chargingStationLocationIdentifier: filters.chargingStationLocationIdentifier,
          } as PtsPaymentTerminalSearchEvent;

          if (filters.serialNumber) {
            searchEvent.query = filters.serialNumber;
          }

          return this.service.getPaymentTerminals(searchEvent).pipe(
            switchMap(terminals => {
              if (filters.serialNumber) {
                this.focusedTerminal.set(terminals.items.find(t => t.serialNumber === filters.serialNumber) || null);
              }

              if (terminals.items.length === 0) {
                return of(terminals);
              }

              const serialNumbers = terminals.items.map(pt => pt.serialNumber);
              return combineLatest([
                of(terminals),
                this.service.getChargingStationInfoForTerminals(serialNumbers),
                this.service.getConnectorsByTerminalSerialNumbers(serialNumbers),
              ]).pipe(
                tap(([terminals, chargingStationLocationInfos, connectorCounts]) => {
                  terminals.items.forEach(paymentTerminal => {
                    const connectorCountsBySerialNumber = connectorCounts
                      .filter(c => c.serialNumber === paymentTerminal.serialNumber)
                      .map(c => c.connectorCount);

                    paymentTerminal.hasConnectorsFromMultipleOrganizations = connectorCountsBySerialNumber.length > 1;

                    const connectorCount = connectorCountsBySerialNumber.reduce((acc, cur) => acc + cur, 0);
                    paymentTerminal.connectorCount = connectorCount;

                    paymentTerminal.chargingStationLocationInfo = chargingStationLocationInfos.find(
                      c => c.serialNumber === paymentTerminal.serialNumber
                    );
                  });

                  if (viewState.sort?.active === 'connectorCount') {
                    const isAsc = viewState.sort?.direction === 'asc';

                    terminals.items.sort(function (a: PtsPaymentTerminal, b: PtsPaymentTerminal) {
                      return (a.connectorCount < b.connectorCount ? -1 : 1) * (isAsc ? 1 : -1);
                    });
                  }
                }),
                map(([terminals]) => terminals)
              );
            }),
            catchError(error => {
              const emptyResult: PagedResult<PtsPaymentTerminal> = {
                items: [],
                totalCount: 0,
                pageIndex: 0,
                pageSize: viewState.page.pageSize,
              };

              return of(emptyResult);
            })
          );
        }),
        tap(terminals => {
          this.terminals.set(terminals);
          this.loading.set(false);
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  refreshTerminal(terminal: PtsPaymentTerminal): void {
    this.refreshTerminalSubject.next(terminal);
  }
}
